const BANCO = 'remoto-audio';
const LOJA = 'trechos';

export interface TrechoNaFila {
  chave: string;
  gravacaoId: string;
  ordem: number;
  inicioMs: number;
  fimMs: number;
  blob: Blob;
  tipo: 'trecho' | 'parte';
}

function abrir(): Promise<IDBDatabase> {
  return new Promise((resolva, recuse) => {
    const pedido = indexedDB.open(BANCO, 1);
    pedido.onupgradeneeded = () => {
      const banco = pedido.result;
      if (!banco.objectStoreNames.contains(LOJA)) {
        const loja = banco.createObjectStore(LOJA, { keyPath: 'chave' });
        loja.createIndex('porGravacao', 'gravacaoId');
      }
    };
    pedido.onsuccess = () => resolva(pedido.result);
    pedido.onerror = () => recuse(pedido.error ?? new Error('nao consegui abrir a fila'));
  });
}

function comLoja<T>(
  modo: IDBTransactionMode,
  acao: (loja: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return abrir().then(
    (banco) =>
      new Promise<T>((resolva, recuse) => {
        const transacao = banco.transaction(LOJA, modo);
        const pedido = acao(transacao.objectStore(LOJA));
        pedido.onsuccess = () => resolva(pedido.result);
        pedido.onerror = () => recuse(pedido.error ?? new Error('falha na fila de audio'));
        transacao.oncomplete = () => banco.close();
      }),
  );
}

/**
 * Fila de envio do áudio gravado, no disco do navegador.
 *
 * Guarda o som, nunca a URL de envio: numa gravação de três horas com a rede
 * caindo no meio, qualquer URL assinada teria vencido muito antes de o trecho
 * conseguir subir. A URL é pedida na hora de tentar de novo.
 */
export const fila = {
  async guardar(item: TrechoNaFila): Promise<void> {
    await comLoja('readwrite', (loja) => loja.put(item));
  },

  async remover(chave: string): Promise<void> {
    await comLoja('readwrite', (loja) => loja.delete(chave));
  },

  async pendentes(gravacaoId: string): Promise<TrechoNaFila[]> {
    const todos = await comLoja<TrechoNaFila[]>('readonly', (loja) => loja.getAll());
    return todos
      .filter((t) => t.gravacaoId === gravacaoId)
      .sort((a, b) => a.ordem - b.ordem);
  },

  /** Tudo que ficou para trás de gravações anteriores, para retomar depois. */
  async tudo(): Promise<TrechoNaFila[]> {
    return comLoja<TrechoNaFila[]>('readonly', (loja) => loja.getAll());
  },
};

export function chaveDe(gravacaoId: string, tipo: 'trecho' | 'parte', ordem: number): string {
  return `${gravacaoId}:${tipo}:${ordem}`;
}
